/**
 * Entity command list — pure builder for the entity command palette
 * (doc 147 §5.3 world-class redesign · PR B).
 *
 * Turns `entityInsights` into a stable, ordered list of `EntityCommand`
 * objects with outcome strings precomputed inline. The palette UI is just
 * a filter + render of this list. Global tools (Persona Slicer, Sandbox,
 * Behavioural Walker, Storyboard) are appended in their own group so they
 * remain reachable from any entity without leaving the palette.
 */
import type { InsightIndex } from './entityInsights.js';
import { entityInsights } from './entityInsights.js';
import type { Route } from './hashRouter.js';

export type CommandGroup = 'entity' | 'quality' | 'generate' | 'tools';

export interface EntityCommand {
  /** Stable id; consumers route on this. */
  id: string;
  group: CommandGroup;
  label: string;
  /** Inline outcome — "1 entity", "2 read · 1 write", "✓ clean", "PII"… */
  outcome: string;
  /** Single-letter shortcut. Empty for global / no-shortcut commands. */
  shortcut: string;
  /** Hash route to navigate to on activation. */
  route: Route;
}

export function buildEntityCommands(
  index: InsightIndex,
  entityId: string,
): EntityCommand[] {
  const ins = entityInsights(index, entityId);
  const cmds: EntityCommand[] = [];

  /* ── Entity-scoped (always present, fixed order) ──────────────────── */
  cmds.push({
    id: 'impact',
    group: 'entity',
    label: `Impact for ${entityId}`,
    outcome:
      ins.blast.entityCount === 0
        ? 'no downstream'
        : `${ins.blast.entityCount} ${
            ins.blast.entityCount === 1 ? 'entity' : 'entities'
          } · ${ins.blast.crossDomainHopCount} cross-domain`,
    shortcut: 'i',
    route: { kind: 'impact', id: entityId },
  });
  cmds.push({
    id: 'trace-from',
    group: 'entity',
    label: `Trace from ${entityId}…`,
    outcome: 'pick a destination',
    shortcut: 't',
    route: { kind: 'trace', from: entityId },
  });
  cmds.push({
    id: 'test-data',
    group: 'entity',
    label: `Generate test data for ${entityId}`,
    outcome: 'seeded fixtures (JSON or TS)',
    shortcut: 'd',
    route: { kind: 'testdata', entityId },
  });
  cmds.push({
    id: 'patterns',
    group: 'entity',
    label: `R/W patterns for ${entityId}`,
    outcome:
      ins.patterns.readerCount === 0 && ins.patterns.writerCount === 0
        ? 'no functions touch this entity'
        : `${ins.patterns.readerCount} read · ${ins.patterns.writerCount} write`,
    shortcut: 'p',
    route: { kind: 'patterns', focus: entityId },
  });

  /* ── Quality & verification ───────────────────────────────────────── */
  cmds.push({
    id: 'lint',
    group: 'quality',
    label: `Lint findings for ${entityId}`,
    outcome:
      ins.quality.lintFindings === 0
        ? '✓ clean · 0 findings'
        : `${ins.quality.lintFindings} ${
            ins.quality.lintFindings === 1 ? 'finding' : 'findings'
          }`,
    shortcut: '',
    route: { kind: 'lint', focus: entityId },
  });
  cmds.push({
    id: 'refactor',
    group: 'quality',
    label: `Refactor proposals for ${entityId}`,
    outcome:
      ins.quality.refactorProposals === 0
        ? '0 candidates'
        : `${ins.quality.refactorProposals} ${
            ins.quality.refactorProposals === 1 ? 'proposal' : 'proposals'
          }`,
    shortcut: '',
    route: { kind: 'refactor', focus: entityId },
  });
  if (ins.quality.piiTags.length > 0) {
    cmds.push({
      id: 'compliance',
      group: 'quality',
      label: `Compliance cone from ${entityId}`,
      outcome: `${ins.quality.piiDownstream} downstream · ${ins.quality.piiTags.join(', ')}`,
      shortcut: '',
      route: { kind: 'compliance', focus: entityId },
    });
  }
  cmds.push({
    id: 'simulate',
    group: 'quality',
    label: `Simulate triggers touching ${entityId}`,
    outcome: 'event + cloud function cascade',
    shortcut: '',
    route: { kind: 'simulate', focus: entityId },
  });

  /* ── Generate & explore ───────────────────────────────────────────── */
  cmds.push({
    id: 'sandbox-seed',
    group: 'generate',
    label: `Sandbox a change to ${entityId}`,
    outcome: 'propose schema edits with impact preview',
    shortcut: '',
    route: { kind: 'sandbox', seedEntity: entityId },
  });
  cmds.push({
    id: 'heatmap-focus',
    group: 'generate',
    label: `${entityId} in heatmap`,
    outcome: 'reference-density row + column',
    shortcut: '',
    route: { kind: 'heatmap', focus: entityId },
  });
  cmds.push({
    id: 'drift-focus',
    group: 'generate',
    label: `${entityId} in drift timeline`,
    outcome: 'recent activity by month',
    shortcut: '',
    route: { kind: 'drift', focus: entityId },
  });

  /* ── Global tools ─────────────────────────────────────────────────── */
  cmds.push({
    id: 'persona-slicer',
    group: 'tools',
    label: 'Persona slicer',
    outcome: '⌘⇧F · filter the model by persona',
    shortcut: '',
    route: { kind: 'workspace', mode: { kind: 'idle' } },
  });
  cmds.push({
    id: 'behavioural-walker',
    group: 'tools',
    label: `Behavioural walker — ${entityId}`,
    outcome: 'state × mode surface matrix filtered to this entity',
    shortcut: '',
    route: { kind: 'behavioral', focus: entityId },
  });
  cmds.push({
    id: 'storyboard',
    group: 'tools',
    label: `Storyboard mode — ${entityId}`,
    outcome: 'walk a user journey involving this entity',
    shortcut: '',
    route: { kind: 'storyboard', focus: entityId },
  });
  cmds.push({
    id: 'sandbox',
    group: 'tools',
    label: 'What-If Sandbox',
    outcome: 'propose schema changes safely',
    shortcut: '',
    route: { kind: 'sandbox' },
  });

  return cmds;
}

/** Substring + id-prefix match. Empty query returns the full list unchanged. */
export function filterCommands(
  commands: readonly EntityCommand[],
  query: string,
): EntityCommand[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...commands];
  const scored: { c: EntityCommand; score: number }[] = [];
  for (const c of commands) {
    const id = c.id.toLowerCase();
    const label = c.label.toLowerCase();
    const outcome = c.outcome.toLowerCase();
    let score = 0;
    if (id === q) score = 1000;
    else if (id.startsWith(q)) score = 600;
    else if (label.startsWith(q)) score = 500;
    else if (label.includes(q)) score = 200;
    else if (outcome.includes(q)) score = 50;
    else if (id.includes(q)) score = 30;
    if (score > 0) scored.push({ c, score });
  }
  return scored.sort((a, b) => b.score - a.score).map((s) => s.c);
}
